import { useEffect } from 'react'

import {
  Address,
  ApplianceFeatures,
  ApplianceType,
  ApplianceVersion,
  IpOutputPort,
  IpPortMode,
  OccupiedPort,
  Output,
  OutputAdminStatus,
  PortMode,
} from 'common/api/v1/types'

import { Select } from '../../../../common/Form'
import UdpForm, { getUdpFieldsToSave } from './UdpForm'
import RtpForm, { getRtpFieldsToSave } from './RtpForm'
import SrtForm, { getSrtFieldsToSave } from './SrtForm'
import ZixiForm, { getZixiFieldsToSave } from './ZixiForm'
import RistForm, { getRistFieldsToSave } from './RistForm'
import RtmpForm, { getRtmpFieldsToSave } from './RtmpForm'
import { isUdpBasedProtocol } from 'common/ports'
import { useFormContext } from 'react-hook-form'
import { AllowUncontrolled } from 'src/components/common/Form/RHF'

export { srtDefaults } from './SrtForm'
export { rtpDefaults } from './RtpForm'
export { udpDefaults } from './UdpForm'
export { zixiDefaults } from './ZixiForm'
export { ristDefaults } from './RistForm'
export { rtmpDefaults } from './RtmpForm'

export enum CommonFields {
  id = 'id',
  mode = 'mode',
  physicalPort = 'physicalPort',
  copies = 'copies',
  region = 'region',
  applianceAllocationId = 'applianceAllocationId',
}

export const getIpPortFormFields = (port: IpOutputPort) => {
  const common = [
    CommonFields.id,
    CommonFields.mode,
    CommonFields.physicalPort,
    CommonFields.copies,
    CommonFields.region,
    CommonFields.applianceAllocationId,
  ]
  switch (port.mode) {
    case IpPortMode.udp:
      return [...common, ...getUdpFieldsToSave()]
    case IpPortMode.rtp:
      return [...common, ...getRtpFieldsToSave()]
    case IpPortMode.zixi:
      return [...common, ...getZixiFieldsToSave(port)]
    case IpPortMode.rist:
      return [...common, ...getRistFieldsToSave()]
    case IpPortMode.srt:
      return [...common, ...getSrtFieldsToSave(port)]
    case IpPortMode.rtmp:
      return [...common, ...getRtmpFieldsToSave()]
  }
  return []
}

export type IpOutput = Output

interface IpPortFormProps {
  applianceId: string
  applianceType: ApplianceType
  applianceVersion: ApplianceVersion
  applianceFeatures: ApplianceFeatures
  index: number
  namePrefix: string
  addresses: Address[]
  occupiedPorts: OccupiedPort[]
  isModeDisabled: boolean
  enforcedPortMode?: PortMode
  onAddLogicalPortRequested: () => void
  onRemoveLogicalPortRequested: () => void
}

const IpPortForm = ({
  addresses,
  applianceId,
  applianceType,
  applianceVersion,
  applianceFeatures,
  index,
  namePrefix,
  occupiedPorts,
  isModeDisabled,
  enforcedPortMode,
  onAddLogicalPortRequested,
  onRemoveLogicalPortRequested,
}: IpPortFormProps) => {
  const { getValues, setValue, watch } = useFormContext<IpOutput & AllowUncontrolled>()

  const modesOptions =
    applianceFeatures.output?.modes
      .filter((m) => m.mode in IpPortMode)
      .map((m) => ({
        name: m.prettyName ?? m.mode,
        value: m.mode,
      })) ?? []
  const modeValues = modesOptions.map((m) => m.value)
  const adminStatus = getValues('adminStatus') ? OutputAdminStatus.on : OutputAdminStatus.off

  const modeKey = `${namePrefix}.mode`
  const currentIpPortMode: IpPortMode = watch(modeKey)
  const isValidMode = enforcedPortMode ? currentIpPortMode === enforcedPortMode : modeValues.includes(currentIpPortMode)

  useEffect(() => {
    if (!isValidMode) {
      // Disallow mixing of port modes for outputs with multiple appliances
      const newMode = enforcedPortMode ?? ''
      if (currentIpPortMode !== newMode) {
        setValue(modeKey, newMode)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValidMode, enforcedPortMode, currentIpPortMode, modeKey])

  return (
    <>
      <Select
        label="Mode"
        name={modeKey}
        newLine
        required
        disabled={isModeDisabled}
        options={modesOptions}
        validators={{
          oneOf: { validValues: new Set(modeValues) },
        }}
      />
      {currentIpPortMode === IpPortMode.udp && <UdpForm namePrefix={namePrefix} addresses={addresses} />}
      {currentIpPortMode === IpPortMode.rtp && (
        <RtpForm
          namePrefix={namePrefix}
          applianceType={applianceType}
          applianceVersion={applianceVersion}
          addresses={addresses}
          applianceFecLimits={applianceFeatures.fec}
        />
      )}
      {currentIpPortMode === IpPortMode.rtmp && <RtmpForm namePrefix={namePrefix} />}
      {currentIpPortMode === IpPortMode.srt && (
        <SrtForm
          namePrefix={namePrefix}
          portIndex={index}
          adminStatus={adminStatus}
          addresses={addresses}
          applianceId={applianceId}
          applianceType={applianceType}
          applianceVersion={applianceVersion}
          applianceFeatures={applianceFeatures}
          occupiedPorts={occupiedPorts.filter((o) => isUdpBasedProtocol(o.portMode))}
          onAddLogicalPortRequested={onAddLogicalPortRequested}
          onRemoveLogicalPortRequested={onRemoveLogicalPortRequested}
        />
      )}
      {currentIpPortMode === IpPortMode.zixi && (
        <ZixiForm
          namePrefix={namePrefix}
          addresses={addresses}
          applianceType={applianceType}
          applianceVersion={applianceVersion}
          applianceFeatures={applianceFeatures}
        />
      )}
      {currentIpPortMode === IpPortMode.rist && <RistForm namePrefix={namePrefix} addresses={addresses} />}
    </>
  )
}

export default IpPortForm
